import { PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../components/Autocomplete";
import { FormId } from "../../../components/enums";
import { defaultCountry } from "../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../store/HOC/genericUI.slice";
import { generateFormattedDate } from "../../../utilities/functions";

import { ComponentId as CircumstancesComponentId } from "./CircumstancesData/enums";
import { ComponentId as DamageComponentId } from "./DamageData/enums";
import { ComponentId as StatementsComponentId } from "./StatementsData/enums";

export type FormDataInputs = {
  [FormId.INCIDENT_CIRCUMSTANCES]: {
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_DATE]: string;
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_HOUR]: string;
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_PLACE]: string;
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_COUNTRY]: AutocompleteItem;
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_PROVINCE]: string;
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_CITY]: string;
    [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_DESCRIPTION]: string;
  };
  [FormId.INCIDENT_DAMAGE]: {
    [DamageComponentId.INCIDENT_DAMAGE_DESCRIPTION]: string;
  };
  [FormId.INCIDENT_STATEMENTS]: {
    [StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT]: string;
    [StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_STATEMENT]: string;
    [StatementsComponentId.INCIDENT_STATEMENTS_POLICE_DEPARTMENT]: string;
    [StatementsComponentId.INCIDENT_STATEMENTS_CASE_NUMBER]: string;
  };
};

export type IncidentCircumstancesFormInputs =
  FormDataInputs[FormId.INCIDENT_CIRCUMSTANCES];
export type IncidentDamageFormInputs = FormDataInputs[FormId.INCIDENT_DAMAGE];
export type IncidentStatementsFormInputs =
  FormDataInputs[FormId.INCIDENT_STATEMENTS];

type IncidentState = {
  data: FormDataInputs;
  ui: UIState;
};

const initialState: IncidentState = {
  data: {
    [FormId.INCIDENT_CIRCUMSTANCES]: {
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_DATE]:
        generateFormattedDate(),
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_HOUR]: "",
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_PLACE]: "",
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_COUNTRY]: {
        ...defaultCountry,
      },
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_PROVINCE]: "",
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_CITY]: "",
      [CircumstancesComponentId.INCIDENT_CIRCUMSTANCES_DESCRIPTION]: "",
    },
    [FormId.INCIDENT_DAMAGE]: {
      [DamageComponentId.INCIDENT_DAMAGE_DESCRIPTION]: "",
    },
    [FormId.INCIDENT_STATEMENTS]: {
      [StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT]: "",
      [StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_STATEMENT]: "",
      [StatementsComponentId.INCIDENT_STATEMENTS_POLICE_DEPARTMENT]: "",
      [StatementsComponentId.INCIDENT_STATEMENTS_CASE_NUMBER]: "",
    },
  },
  ui: {
    validated: false,
    "circumstances[country]": defaultCountry.value,
  },
};

const incidentSlice = createGenericUISlice({
  name: "@AC/incident",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      const {
        payload: {
          circumstances: { date, hour, ...rest },
          damage,
          statements,
        },
      } = action;

      state.data[FormId.INCIDENT_CIRCUMSTANCES] = {
        date: date.toString(),
        hour: hour.toString(),
        ...rest,
      };
      state.data[FormId.INCIDENT_DAMAGE] = {
        ...initialState.data[FormId.INCIDENT_DAMAGE],
        ...damage,
      };
      state.data[FormId.INCIDENT_STATEMENTS] = {
        ...initialState.data[FormId.INCIDENT_STATEMENTS],
        ...statements,
      };
      state.ui.validated = true;
    },
    resetData: (state) => initialState,
    setPolicePresence: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_STATEMENTS][
        StatementsComponentId.INCIDENT_STATEMENTS_POLICE_PRESENT
      ] = action.payload;
      state.data[FormId.INCIDENT_STATEMENTS][
        StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_STATEMENT
      ] = "";
    },
    setPerpetratorStatement: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_STATEMENTS][
        StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_STATEMENT
      ] = action.payload;
    },
  },
});

export const {
  setData: setIncidentFormData,
  resetData: resetIncidentData,
  setPerpetratorStatement,
  setPolicePresence,
  setUIField,
} = incidentSlice.actions;

export const { reducer } = incidentSlice;
